
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendOTP, verifyOTP, verifyAadhaar, loginUser } from '../../services/authService';
import { showError, showSuccess } from '../../utils/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHospital, faPhone, faLock, faEye, faEyeSlash, faIdCard } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { setAuth } from '../../slices/authSlice'; // 👈 Add this

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    phoneNumber: '',
    otp: '',
    aadhaar: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showAadhaar, setShowAadhaar] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [ashaName, setashaName] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await sendOTP({ phoneNumber: formData.phoneNumber });
      setOtpSent(true);
      setShowOTP(true);
      setashaName(data.name);
      showSuccess('OTP sent successfully!');
    } catch (err) {
      console.error(err);
      showError(err.message || 'Failed to send OTP');
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError('');
    try {
      await verifyOTP({ phoneNumber: formData.phoneNumber, otp: formData.otp });
      setShowAadhaar(true);
      showSuccess('OTP verified successfully!');
    } catch (err) {
      console.error(err);
      showError(err.message || 'Invalid OTP');
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    try {
      const response = await verifyAadhaar({
        phoneNumber: formData.phoneNumber,
        aadhaar: formData.aadhaar,
      });
  
      if (!response) {
        throw new Error('Invalid response from server');
      }
  
      const { token, phoneNumber, asha_id } = response.data;
  
      // Optional: store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', phoneNumber);
      localStorage.setItem('asha_id', asha_id);
  
      // ✅ Set Redux state
      dispatch(setAuth({ token, phoneNumber, asha_id }));
  
      if (rememberMe) {
        localStorage.setItem('rememberedPhoneNumber', formData.phoneNumber);
      } else {
        localStorage.removeItem('rememberedPhoneNumber');
      }
  
      showSuccess('Login successful! 👏');
      navigate('/');
    } catch (err) {
      console.error(err);
      showError(err.message || 'Login failed');
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };  

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="loginPage">
      <div className="login-container">
        <div className="logo">
          <img className="asha_logo_login" src="/assest/logo/logo.png" alt="Logo" />
        </div>
        <h1 className="title">ASHA Worker System</h1>
        <p className="subtitle">Health Worker Portal</p>
     <p className="subtitle">{ashaName ? `Welcome ${ashaName}` : ''}</p>

     
        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="input-field">
            <FontAwesomeIcon icon={faPhone} className="icon" />
            <input
              type="tel"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              disabled={otpSent}
            />
          </div>

          {!otpSent ? (
            <button
              type="button"
              className="login-button"
              onClick={handleSendOTP}
              disabled={loading || !formData.phoneNumber}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          ) : (
            <>
              {showOTP && !showAadhaar && (
                <div className="input-field">
                  <FontAwesomeIcon icon={faLock} className="icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="otp"
                    placeholder="Enter OTP"
                    value={formData.otp}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={togglePasswordVisibility}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              )}

              {showOTP && !showAadhaar && (
                <button
                  type="button"
                  className="login-button"
                  onClick={handleVerifyOTP}
                  disabled={loading || !formData.otp}
                >
                  {loading ? 'Verifying OTP...' : 'Verify OTP'}
                </button>
              )}

              {showAadhaar && (
                <div className="input-field">
                  <FontAwesomeIcon icon={faIdCard} className="icon" />
                  <input
                    type="text"
                    name="aadhaar"
                    placeholder="Aadhaar Number"
                    value={formData.aadhaar}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              {showAadhaar && (
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading || !formData.aadhaar}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              )}
            </>
          )}

          <div className="options-row">
            <div className="remember-me">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember">Remember me</label>
            </div>
            <a href="/forgot-password" className="forgot-password">Forgot Password?</a>
          </div>
        </form>

        <a href="/register" className="help-link">Sign Up</a>
        <div className="login_flag">
          <img className="flag_image" src="/assest/logo/flag.png" alt="Logo" />
          <span>Proudly Made in India</span>
          <img className="flag_image" src="/assest/logo/flag.png" alt="Logo" />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;